import React from 'react';
import ActionButton from '../ActionButton/ActionButton';
import ListOfGuesses from '../ListOfGuesses/ListOfGuesses';
import { motion } from 'framer-motion';
import { sample, checkGuess, isRealWord } from '../../utils';
import ENGLISH_WORDS from '../../english_words.json';
import SPANISH_WORDS from '../../spanish_words.json';
import RUSSIAN_WORDS from '../../russian_words.json';
import WinBanner from '../WinBanner/WinBanner';
import LoseBanner from '../LoseBanner';
import { GUESSES_LIMIT } from '../../gameSettings';
import { useKeyPress } from '../../hooks/useKeyPress';
import Keyboard from '../Keyboard/Keyboard';
import NotificationBanner from '../NotificationBanner/NotificationBanner';
import { LanguageContext } from '../App';
import localisation from '../../localisation.json';
import { RestartContext } from '../RestartContext/RestartContext';

function Game() {
    const { language } = React.useContext(LanguageContext);
    const { isRestartTriggered, reseRestartTrigger } =
        React.useContext(RestartContext);
    let dictionary;
    let allLetters = localisation[0].allLetters[language];

    switch (language) {
        case 'EN':
            dictionary = ENGLISH_WORDS;
            break;
        case 'ES':
            dictionary = SPANISH_WORDS;
            break;
        case 'RU':
            dictionary = RUSSIAN_WORDS;
            break;
        default:
            dictionary = ENGLISH_WORDS;
            break;
    }
    const [allGuesses, setAllGuesses] = React.useState([]);
    const [gameStatus, setGameStatus] = React.useState('playing');
    const [isButtonFocused, setIsButtonFocused] = React.useState(false);

    const [answer, setAnswer] = React.useState(() =>
        sample(dictionary).toUpperCase()
    );
    const [typedWord, setTypedWord] = React.useState('');
    const [shortAnswerAnimation, setShortAnswerAnimation] =
        React.useState(false);
    const [fakeWordAnimation, setFakeWordAnimation] = React.useState(false);
    const validatedGuesses = React.useMemo(
        () => allGuesses.map((guess) => checkGuess(guess.value, answer)),
        [allGuesses, answer]
    );
    const pressedLetter = useKeyPress(allLetters);
    const handleLetterPress = React.useCallback(
        (key) => {
            if (gameStatus !== 'playing') return;
            const handleAddGuessToList = (guess) => {
                const nextGuesses = [...allGuesses, guess];
                setAllGuesses(nextGuesses);
                if (guess.value.toUpperCase() === answer.toUpperCase()) {
                    const numberOfWins =
                        Number(localStorage.getItem('numberOfWins')) || 0;
                    localStorage.setItem('numberOfWins', numberOfWins + 1);
                    setGameStatus('win');
                    const numberOfStreaks =
                        Number(localStorage.getItem('numberOfStreaks')) || 0;

                    localStorage.setItem(
                        'numberOfStreaks',
                        numberOfStreaks + 1
                    );
                } else if (nextGuesses.length >= GUESSES_LIMIT) {
                    const numberOfLosses =
                        Number(localStorage.getItem('numberOfLosses')) || 0;
                    localStorage.setItem('numberOfLosses', numberOfLosses + 1);
                    localStorage.removeItem('numberOfStreaks');
                    setGameStatus('loss');
                }
            };
            const handleSubmitGuess = () => {
                if (typedWord.length < 5) {
                    setShortAnswerAnimation(true);
                    return;
                }
                if (!isRealWord(typedWord, language)) {
                    setFakeWordAnimation(true);
                    return;
                }
                const guess = {
                    id: crypto.randomUUID(),
                    value: typedWord.slice(0, 5),
                };
                handleAddGuessToList(guess);
                setTypedWord('');
            };

            if (
                key.toUpperCase() === 'DELETE' ||
                key.toUpperCase() === 'BACKSPACE'
            ) {
                setTypedWord((prev) => prev.slice(0, -1));
            } else if (key.toUpperCase() === 'ENTER') {
                if (!isButtonFocused) handleSubmitGuess();
            } else {
                if (typedWord.length === 5) {
                    return;
                }
                setTypedWord((prev) => prev + key);
            }
            pressedLetter.resetPressedKey();
        },
        [
            gameStatus,
            pressedLetter,
            allGuesses,
            answer,
            typedWord,
            language,
            isButtonFocused,
        ]
    );

    React.useEffect(() => {
        handleLetterPress(pressedLetter.pressedKey);
    }, [pressedLetter.pressedKey, handleLetterPress]);

    const handleRestart = React.useCallback(() => {
        setAnswer(sample(dictionary).toUpperCase());
        setAllGuesses([]);
        setGameStatus('playing');
        reseRestartTrigger();
    }, [dictionary, reseRestartTrigger]);

    React.useEffect(() => {
        if (isRestartTriggered) handleRestart();
    }, [handleRestart, isRestartTriggered]);

    return (
        <div>
            <div
                className={`notification-wrapper ${
                    !fakeWordAnimation ? 'hidden' : ''
                }`}>
                <NotificationBanner
                    isAnimated={fakeWordAnimation}
                    setIsAnimated={setFakeWordAnimation}>
                    {localisation[0].wordDoesntExistNotification[language]}
                </NotificationBanner>
            </div>
            <div
                className={`notification-wrapper ${
                    !shortAnswerAnimation ? 'hidden' : ''
                }`}>
                <NotificationBanner
                    isAnimated={shortAnswerAnimation}
                    setIsAnimated={setShortAnswerAnimation}>
                    {localisation[0].tooShortNotification[language]}
                </NotificationBanner>
            </div>
            <motion.div
                className='game-wrapper'
                animate={{
                    rotate: shortAnswerAnimation ? [5, -5, 5, -5, 0] : [0, 0],
                    origin: 'center',
                }}
                transition={{
                    duration: 0.3,
                    ease: 'easeInOut',
                }}
                initial={false}>
                <ListOfGuesses
                    validatedGuesses={validatedGuesses}
                    currentGuess={typedWord}
                />
                {gameStatus === 'win' ? (
                    <WinBanner numOfGuesses={allGuesses.length}>
                        <ActionButton clickHandler={handleRestart}>
                            {localisation[0].startNewGameButton[language]}
                        </ActionButton>
                    </WinBanner>
                ) : null}
                {gameStatus === 'loss' ? (
                    <LoseBanner correctAnswer={answer.toUpperCase()}>
                        <ActionButton clickHandler={handleRestart}>
                            {localisation[0].startNewGameButton[language]}
                        </ActionButton>
                    </LoseBanner>
                ) : null}
            </motion.div>
            <Keyboard
                handleLetterPress={(letter) => handleLetterPress(letter)}
                validatedGuesses={validatedGuesses}
                setIsButtonFocused={setIsButtonFocused}
                isButtonFocused={isButtonFocused}
            />
        </div>
    );
}

export default Game;
